import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Avatar,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  InputBase,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Badge,
  Rating,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Store as StoreIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Star as StarIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  FilterList as FilterListIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import BottomNavigation from '../components/BottomNavigation';
import { homeAPI } from '../services/api';

// Styled components - same as Home screen
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '20ch',
    },
  },
}));

const Offers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get offer type from navigation state
  const offerType = location.state?.offerType || 'featured';

  // State variables
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProviderTypes, setSelectedProviderTypes] = useState(['salon', 'pros']); // Default both selected
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [distanceType, setDistanceType] = useState('entire-city');
  const [distanceValue, setDistanceValue] = useState(10);
  const [minRating, setMinRating] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [sortBy, setSortBy] = useState('rating-high');

  // State for offers data
  const [offersData, setOffersData] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [offersError, setOffersError] = useState(null);

  // Fetch offers data from backend
  const fetchOffersData = async () => {
    try {
      setOffersLoading(true);
      setOffersError(null);
      
      let offers = [];
      
      if (offerType === 'featured') {
        // For featured services, use the featured services API
        console.log('🔍 Fetching featured services...');
        const response = await homeAPI.getFeaturedServices();
        console.log('🔍 Featured services API response:', response);
        
        if (response.data && response.data.services) {
          console.log('🔍 Raw featured services:', response.data.services);
          offers = response.data.services.map(service => ({
            ...service,
            offerType: 'featured',
            // Ensure consistent field names for UI rendering
            serviceName: service.serviceName || service.name || 'Service',
            providerName: service.providerName || service.provider?.businessName || 'Provider',
            providerType: service.providerType || service.provider?.providerType || 'salon',
            rating: service.rating || service.averageRating || 4.5,
            price: service.price || service.basePrice || 0,
            distance: service.distance || '2.5 km',
            duration: service.duration || '60 min',
            discount: service.originalPrice && service.price ? 
              `${Math.round(((service.originalPrice - service.price) / service.originalPrice) * 100)}% OFF` : 
              'Featured Service'
          }));
          console.log('🔍 Processed featured services:', offers);
        } else {
          console.log('❌ No services found in response:', response);
        }
      } else {
        // For other offer types (today, week, month), use the offers by type API
        console.log(`🔍 Fetching ${offerType} offers...`);
        const response = await homeAPI.getOffersByType(offerType);
        console.log(`🔍 ${offerType} offers API response:`, response);
        
        if (response.data && response.data.offers) {
          console.log(`🔍 Raw ${offerType} offers:`, response.data.offers);
          offers = response.data.offers.map(offer => ({
            ...offer,
            offerType: offerType,
            // Ensure consistent field names for UI rendering
            serviceName: offer.serviceName || offer.name || offer.service?.name || 'Service',
            providerName: offer.providerName || offer.provider?.businessName || offer.provider?.name || 'Provider',
            providerType: offer.providerType || offer.provider?.providerType || offer.provider?.type || 'salon',
            rating: offer.rating || offer.averageRating || offer.provider?.rating || 4.5,
            price: offer.price || offer.basePrice || offer.service?.price || 0,
            distance: offer.distance || '2.5 km',
            duration: offer.duration || offer.service?.duration || '60 min',
            discount: offer.discount || offer.discountPercentage || 
              (offer.originalPrice && offer.price ? 
                `${Math.round(((offer.originalPrice - offer.price) / offer.originalPrice) * 100)}% OFF` : 
                `${offerType.charAt(0).toUpperCase() + offerType.slice(1)} Offer`)
          }));
          console.log(`🔍 Processed ${offerType} offers:`, offers);
        } else {
          console.log(`❌ No ${offerType} offers found in response:`, response);
        }
      }
      
      setOffersData(offers);
    } catch (error) {
      console.error(`Error fetching ${offerType} offers:`, error);
      setOffersError(`Failed to fetch ${offerType} offers. Please try again later.`);
      setOffersData([]);
    } finally {
      setOffersLoading(false);
    }
  };

  // Fetch offers when component mounts or offerType changes
  useEffect(() => {
    fetchOffersData();
  }, [offerType]);

  // Initialize filtered results when offers data are loaded
  useEffect(() => {
    if (offersData.length > 0) {
      setFilteredResults(offersData);
    }
  }, [offersData]);

  // Get offers data based on current state
  const getOffersData = () => {
    return offersData;
  };

  // Handle provider type selection
  const handleProviderTypeToggle = (providerType) => {
    setSelectedProviderTypes(prev => 
      prev.includes(providerType)
        ? prev.filter(type => type !== providerType)
        : [...prev, providerType]
    );
  };

  // Get user location from localStorage (set by home screen)
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      try {
        setUserLocation(JSON.parse(savedLocation));
      } catch (error) {
        console.error('Error parsing user location:', error);
      }
    }
  }, []);

  // Filter results based on search, provider types, distance, and rating
  useEffect(() => {
    if (offersData.length === 0) return; // Don't filter if no data yet
    
    setLoading(true);
    setPage(1);
    
    const rawData = getOffersData();
    console.log('🔍 Raw data before filtering:', rawData);
    console.log('🔍 Filtering for offerType:', offerType);
    
    let filtered = rawData.filter(result => {
      // Advanced search filter - similar to FeaturedServices
      let matchesSearch = true;
      if (searchQuery.trim()) {
        const searchWords = searchQuery.toLowerCase().trim().split(/\s+/).filter(word => word.length > 0);
        console.log('🔍 Filtering by search words:', searchWords);
        
        // Search across multiple fields: service name, provider name, provider type, service description, etc.
        const serviceName = (result.serviceName || result.name || '').toLowerCase();
        const providerName = (result.providerName || result.provider?.businessName || result.provider?.name || '').toLowerCase();
        const providerType = (result.providerType || result.provider?.providerType || result.provider?.type || '').toLowerCase();
        const serviceDescription = (result.description || result.service?.description || '').toLowerCase();
        const serviceCategory = (result.category || result.service?.category || '').toLowerCase();
        
        // Check if ANY search word is found in any of the searchable fields (OR logic)
        matchesSearch = searchWords.some(searchWord => {
          return serviceName.includes(searchWord) ||
                 providerName.includes(searchWord) ||
                 providerType.includes(searchWord) ||
                 serviceDescription.includes(searchWord) ||
                 serviceCategory.includes(searchWord);
        });
        
        if (matchesSearch) {
          console.log('✅ Offer matches search:', result.serviceName || result.name, {
            searchWords,
            serviceName,
            providerName,
            providerType,
            serviceDescription,
            serviceCategory
          });
        }
      }
      
      // Handle provider type filtering - check both direct and nested providerType
      const resultProviderType = result.providerType || result.provider?.providerType || result.provider?.type;
      const matchesProviderType = selectedProviderTypes.includes(resultProviderType);
      
      // Filter by rating (whole number ranges)
      let matchesRating = true;
      if (minRating > 0) {
        const minRatingValue = minRating;
        const maxRatingValue = minRating + 0.9;
        const rating = result.rating || result.averageRating || 0;
        matchesRating = rating >= minRatingValue && rating <= maxRatingValue;
      }
      
      // Filter by distance based on user location
      let matchesDistance = true;
      if (distanceType === 'distance' && userLocation && result.distance) {
        const distanceNum = parseFloat(result.distance.replace(' km', ''));
        matchesDistance = distanceNum <= distanceValue;
      }
      
      return matchesSearch && matchesProviderType && matchesRating && matchesDistance;
    });
    
    console.log('🔍 Filtered results:', filtered);
    console.log('🔍 Filtered results length:', filtered.length);
    
    // Sort results
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating-high':
          return (b.rating || 0) - (a.rating || 0);
        case 'rating-low':
          return (a.rating || 0) - (b.rating || 0);
        case 'distance':
          const distanceA = parseFloat((a.distance || '0 km').replace(' km', ''));
          const distanceB = parseFloat((b.distance || '0 km').replace(' km', ''));
          return distanceA - distanceB;
        case 'name':
          return (a.serviceName || a.name || '').localeCompare(b.serviceName || b.name || '');
        case 'price-low':
          return parseFloat((a.price || '0').toString().replace('$', '')) - parseFloat((b.price || '0').toString().replace('$', ''));
        case 'price-high':
          return parseFloat((b.price || '0').toString().replace('$', '')) - parseFloat((a.price || '0').toString().replace('$', ''));
        default:
          return (b.rating || 0) - (a.rating || 0);
      }
    });
    
    setFilteredResults(filtered);
    setLoading(false);
  }, [offersData, searchQuery, selectedProviderTypes, distanceType, distanceValue, minRating, sortBy, userLocation, offerType]);

  // Infinite scroll functionality
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        if (hasMore && !loading) {
          loadMore();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading]);

  // Load more results (infinite scroll simulation)
  const loadMore = () => {
    if (hasMore && !loading) {
      setLoading(true);
      setTimeout(() => {
        setPage(prev => prev + 1);
        setHasMore(page < 3);
        setLoading(false);
      }, 1000);
    }
  };

  // Auto-scroll images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => {
        const newIndex = {};
        getOffersData().forEach(result => {
          newIndex[result.id] = ((prev[result.id] || 0) + 1) % 1; // Single image for now
        });
        return newIndex;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [offerType]);

  // Dynamic placeholder
  useEffect(() => {
    const placeholders = [
      "Search for services...",
      "Search for offers...", 
      "Search for discounts...",
      "Search for treatments..."
    ];
    
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholders.length);
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // Clear all filters
  const clearFilters = () => {
    setSelectedProviderTypes(['salon', 'pros']);
    setSearchQuery('');
    setDistanceType('entire-city');
    setDistanceValue(10);
    setMinRating(0);
    setSortBy('rating-high');
  };

  // Display current location info
  const getLocationInfo = () => {
    if (userLocation) {
      if (userLocation.fullAddress) {
        return `📍 ${userLocation.fullAddress}`;
      } else if (userLocation.address) {
        return `📍 ${userLocation.address}`;
      } else if (userLocation.city) {
        return `📍 ${userLocation.city}`;
      } else {
        return '📍 Current Location';
      }
    }
    return '📍 Location not set';
  };

  // Get offer type title
  const getOfferTypeTitle = () => {
    switch (offerType) {
      case 'featured':
        return 'Featured Services';
      case 'today':
        return "Today's Offers";
      case 'week':
        return "This Week's Offers";
      case 'month':
        return "This Month's Offers";
      default:
        return 'Offers';
    }
  };

  // Get offer type color
  const getOfferTypeColor = () => {
    switch (offerType) {
      case 'featured':
        return '#1976d2';
      case 'today':
        return '#d32f2f';
      case 'week':
        return '#1976d2';
      case 'month':
        return '#7b1fa2';
      default:
        return '#1976d2';
    }
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ backgroundColor: getOfferTypeColor() }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {getOfferTypeTitle()}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          py: 2 
        }}>
        {/* Search Bar with Filter */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Search sx={{ flex: 1 }}>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder={[
                  "Search for services...",
                  "Search for offers...", 
                  "Search for discounts...",
                  "Search for treatments...",
                  "Search for provider names...",
                  "Search for service categories..."
                ][placeholderIndex]}
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  console.log('🔍 Search input changed:', value);
                  setSearchQuery(value);
                }}
                sx={{ width: '100%' }}
              />
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                  sx={{ 
                    position: 'absolute', 
                    right: 8, 
                    color: 'rgba(0,0,0,0.5)',
                    '&:hover': { color: 'rgba(0,0,0,0.8)' }
                  }}
                >
                  <ClearIcon />
                </IconButton>
              )}
            </Search>
                         <IconButton
               onClick={() => setFilterDialogOpen(true)}
               sx={{ 
                 backgroundColor: getOfferTypeColor(),
                 color: 'white',
                 borderRadius: '50%',
                 '&:hover': { 
                   backgroundColor: getOfferTypeColor() + 'dd',
                   transform: 'scale(1.05)',
                   boxShadow: `0 4px 12px ${getOfferTypeColor()}40`
                 },
                 transition: 'all 0.2s ease'
               }}
             >
               <FilterListIcon />
             </IconButton>
          </Box>
          {/* Location Info */}
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
              {getLocationInfo()}
            </Typography>
            {distanceType === 'distance' && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                • Within {distanceValue}km radius
              </Typography>
            )}
            {distanceType === 'entire-city' && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                • Entire city (no distance limit)
              </Typography>
            )}
          </Box>
          
          {/* Search Results Counter */}
          {searchQuery.trim() && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="primary" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                🔍 Search: "{searchQuery}" - {filteredResults.length} results found
              </Typography>
            </Box>
          )}
          
          {/* Search Help Text */}
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              💡 Search by service name, provider name, provider type, service description, or categories
            </Typography>
          </Box>
        </Paper>

        {/* Provider Type Filter */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 2 }}>
            Provider Type
          </Typography>
                     <Box sx={{ display: 'flex', gap: 2 }}>
             <Button
               variant={selectedProviderTypes.includes('salon') ? 'contained' : 'outlined'}
               startIcon={<StoreIcon />}
               onClick={() => handleProviderTypeToggle('salon')}
               sx={{
                 flex: 1,
                 backgroundColor: selectedProviderTypes.includes('salon') ? getOfferTypeColor() : 'transparent',
                 color: selectedProviderTypes.includes('salon') ? 'white' : getOfferTypeColor(),
                 borderColor: getOfferTypeColor(),
                 borderRadius: '25px',
                 py: 1.5,
                 fontWeight: 'bold',
                 '&:hover': {
                   backgroundColor: selectedProviderTypes.includes('salon') ? getOfferTypeColor() + 'dd' : getOfferTypeColor() + '15',
                   transform: 'translateY(-1px)',
                   boxShadow: selectedProviderTypes.includes('salon') ? `0 4px 12px ${getOfferTypeColor()}40` : `0 2px 8px ${getOfferTypeColor()}30`
                 },
                 transition: 'all 0.2s ease'
               }}
             >
               Salon
             </Button>
             <Button
               variant={selectedProviderTypes.includes('pros') ? 'contained' : 'outlined'}
               startIcon={<PersonIcon />}
               onClick={() => handleProviderTypeToggle('pros')}
               sx={{
                 flex: 1,
                 backgroundColor: selectedProviderTypes.includes('pros') ? getOfferTypeColor() : 'transparent',
                 color: selectedProviderTypes.includes('pros') ? 'white' : getOfferTypeColor(),
                 borderColor: getOfferTypeColor(),
                 borderRadius: '25px',
                 py: 1.5,
                 fontWeight: 'bold',
                 '&:hover': {
                   backgroundColor: selectedProviderTypes.includes('pros') ? getOfferTypeColor() + 'dd' : getOfferTypeColor() + '15',
                   transform: 'translateY(-1px)',
                   boxShadow: selectedProviderTypes.includes('pros') ? `0 4px 12px ${getOfferTypeColor()}40` : `0 2px 8px ${getOfferTypeColor()}30`
                 },
                 transition: 'all 0.2s ease'
               }}
             >
               Pros
             </Button>
           </Box>
        </Box>

        {/* Results Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
              {getOfferTypeTitle()}
              {selectedProviderTypes.length > 0 && selectedProviderTypes.length < 2 && (
                <span style={{ fontSize: '0.9em', color: '#666', fontWeight: 'normal' }}>
                  {' '}({selectedProviderTypes[0] === 'salon' ? 'Salon' : 'Pros'})
                </span>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredResults.length} results
            </Typography>
          </Box>

                     {offersLoading ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
               <CircularProgress />
               <Typography variant="body2" color="text.secondary" sx={{ mt: 2, ml: 2 }}>
                 Loading {offerType === 'featured' ? 'featured services' : `${offerType} offers`}...
               </Typography>
             </Box>
          ) : offersError ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="error">
                {offersError}
              </Typography>
              <Button 
                variant="outlined" 
                onClick={fetchOffersData}
                sx={{ mt: 2 }}
              >
                Try Again
              </Button>
            </Paper>
          ) : loading && page === 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
                     ) : filteredResults.length === 0 ? (
             <Paper sx={{ p: 4, textAlign: 'center' }}>
               <Typography variant="h6" color="text.secondary">
                 No {offerType === 'featured' ? 'featured services' : `${offerType} offers`} found
               </Typography>
               <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                 Try adjusting your search or filters
               </Typography>
             </Paper>
          ) : (
            <>
              <Grid container spacing={2}>
                {filteredResults.map((result) => (
                  <Grid item xs={12} sm={6} md={4} key={result.id}>
                                         <Card 
                       onClick={() => navigate(`/service/${result._id || result.id}`)}
                       sx={{ 
                         height: '100%', 
                         cursor: 'pointer',
                         transition: 'all 0.3s ease',
                         '&:hover': {
                           transform: 'translateY(-4px)',
                           boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                         },
                         borderRadius: 3,
                         overflow: 'hidden',
                         border: 'none',
                         position: 'relative',
                         backgroundColor: 'white'
                       }}
                     >
                       {/* Image Section */}
                       <Box sx={{ position: 'relative', height: 200 }}>
                         <CardMedia
                           component="img"
                           height="200"
                           image={result.image || result.displayImage || 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=200&fit=crop'}
                           alt={result.serviceName || result.name}
                           sx={{ 
                             objectFit: 'cover',
                             transition: 'transform 0.3s ease',
                             '&:hover': {
                               transform: 'scale(1.05)'
                             }
                           }}
                         />
                         
                                                   {/* Discount Badge */}
                          <Box sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            backgroundColor: getOfferTypeColor(),
                            color: 'white',
                            borderRadius: '20px',
                            px: 1.5,
                            py: 0.5,
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            boxShadow: `0 2px 8px ${getOfferTypeColor()}40`
                          }}>
                            {result.discount}
                          </Box>
                         
                                                   {/* Favorite Button */}
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              // TODO: Add favorite functionality
                            }}
                            size="small"
                            sx={{ 
                              position: 'absolute', 
                              top: 12, 
                              left: 12,
                              backgroundColor: 'rgba(255,255,255,0.95)',
                              color: getOfferTypeColor(),
                              backdropFilter: 'blur(10px)',
                              '&:hover': { 
                                backgroundColor: 'rgba(255,255,255,1)',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <FavoriteBorderIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                         
                         
                       </Box>
                       
                       {/* Content Section */}
                       <CardContent sx={{ p: 2 }}>
                         {/* Service Name */}
                         <Typography variant="h6" sx={{ 
                           fontWeight: 'bold', 
                           fontSize: '1rem',
                           lineHeight: 1.3,
                           mb: 1.5,
                           color: '#1a1a1a'
                         }}>
                           {result.serviceName || result.name || 'Service'}
                         </Typography>
                         
                         {/* Provider Info with Type Tag */}
                         <Box sx={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           justifyContent: 'space-between',
                           mb: 1.5
                         }}>
                           <Typography variant="body2" color="text.secondary" sx={{ 
                             fontSize: '0.85rem',
                             fontWeight: 500
                           }}>
                             by {result.providerName || 'Provider'}
                           </Typography>
                           
                           {/* Provider Type Tag */}
                           <Box sx={{ 
                             display: 'inline-flex',
                             alignItems: 'center',
                             gap: 0.5,
                             backgroundColor: getOfferTypeColor() + '15',
                             color: getOfferTypeColor(),
                             borderRadius: '15px',
                             px: 1,
                             py: 0.5
                           }}>
                             {(result.providerType === 'salon' || result.provider?.providerType === 'salon') ? (
                               <StoreIcon sx={{ fontSize: 14 }} />
                             ) : (
                               <PersonIcon sx={{ fontSize: 14 }} />
                             )}
                             <Typography variant="caption" sx={{ 
                               fontWeight: 'bold',
                               fontSize: '0.7rem'
                             }}>
                               {(result.providerType === 'salon' || result.provider?.providerType === 'salon') ? 'Salon' : 'Pros'}
                             </Typography>
                           </Box>
                         </Box>
                         
                         {/* Combined Info Row */}
                         <Box sx={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           gap: 2,
                           mb: 2,
                           flexWrap: 'wrap'
                         }}>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                             <StarIcon sx={{ color: getOfferTypeColor(), fontSize: 16 }} />
                             <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                               {result.rating || result.averageRating || 4.5}
                             </Typography>
                           </Box>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                             <TimeIcon sx={{ color: getOfferTypeColor(), fontSize: 16 }} />
                             <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                               {result.duration || '60 min'}
                             </Typography>
                           </Box>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                             <LocationIcon sx={{ color: getOfferTypeColor(), fontSize: 16 }} />
                             <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                               {result.distance || '2.5 km'}
                             </Typography>
                           </Box>
                         </Box>
                         
                         {/* Price and Action Section */}
                         <Box sx={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           justifyContent: 'space-between',
                           mb: 1.5
                         }}>
                           <Box>
                             <Typography variant="h5" sx={{ 
                               fontWeight: 'bold', 
                               color: getOfferTypeColor(), 
                               fontSize: '1.25rem',
                               lineHeight: 1
                             }}>
                               ${result.price || result.basePrice || 0}
                             </Typography>
                             {result.originalPrice && (
                               <Typography variant="body2" sx={{ 
                                 textDecoration: 'line-through', 
                                 color: '#999', 
                                 fontSize: '0.8rem',
                                 mt: 0.5
                               }}>
                                 ${result.originalPrice}
                               </Typography>
                             )}
                           </Box>
                                                       <Button
                              variant="contained"
                              size="medium"
                              startIcon={<AddIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Add to cart functionality can be implemented here
                              }}
                              sx={{ 
                                backgroundColor: getOfferTypeColor(),
                                borderRadius: '25px',
                                px: 2,
                                py: 1,
                                '&:hover': { 
                                  backgroundColor: getOfferTypeColor() + 'dd',
                                  transform: 'translateY(-1px)',
                                  boxShadow: `0 4px 12px ${getOfferTypeColor()}40`
                                },
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                textTransform: 'none',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              Add to Cart
                            </Button>
                         </Box>
                         
                                                   {/* Valid Until Badge */}
                          {result.validUntil && (
                            <Box sx={{
                              backgroundColor: getOfferTypeColor() + '15',
                              color: getOfferTypeColor(),
                              borderRadius: '15px',
                              px: 1.5,
                              py: 0.5,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}>
                              <TimeIcon sx={{ fontSize: 14 }} />
                              <Typography variant="caption" sx={{ 
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                              }}>
                                {result.validUntil}
                              </Typography>
                            </Box>
                          )}
                       </CardContent>
                     </Card>
                  </Grid>
                ))}
              </Grid>
              
              {/* Loading indicator for infinite scroll */}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Filter Dialog */}
      <Dialog 
        open={filterDialogOpen} 
        onClose={() => setFilterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Filter & Sort
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Provider Type Filter */}
            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Provider Type
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedProviderTypes.includes('salon')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProviderTypes([...selectedProviderTypes, 'salon']);
                        } else {
                          setSelectedProviderTypes(selectedProviderTypes.filter(type => type !== 'salon'));
                        }
                      }}
                    />
                  }
                  label="Salon"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedProviderTypes.includes('pros')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedProviderTypes([...selectedProviderTypes, 'pros']);
                        } else {
                          setSelectedProviderTypes(selectedProviderTypes.filter(type => type !== 'pros'));
                        }
                      }}
                    />
                  }
                  label="Pros"
                />
              </FormGroup>
            </FormControl>

            {/* Distance Filter */}
            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Distance
              </Typography>
              <RadioGroup
                value={distanceType}
                onChange={(e) => setDistanceType(e.target.value)}
              >
                <FormControlLabel
                  control={<Radio />}
                  label="Entire city"
                  value="entire-city"
                />
                <FormControlLabel
                  control={<Radio />}
                  label="Distance"
                  value="distance"
                />
              </RadioGroup>
              {distanceType === 'distance' && (
                <Box sx={{ mt: 2, px: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Maximum distance: {distanceValue} km
                  </Typography>
                  <Slider
                    value={distanceValue}
                    onChange={(e, newValue) => setDistanceValue(newValue)}
                    min={1}
                    max={30}
                    marks={[
                      { value: 1, label: '1km' },
                      { value: 15, label: '15km' },
                      { value: 30, label: '30km' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                </Box>
              )}
            </FormControl>

            {/* Rating Filter */}
            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                Rating Filter
              </Typography>
              <Box sx={{ px: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {minRating === 0 ? 'Any rating' : `${minRating}+ stars`}
                </Typography>
                <Slider
                  value={minRating}
                  onChange={(e, newValue) => setMinRating(newValue)}
                  min={0}
                  max={5}
                  step={1}
                  marks={[
                    { value: 0, label: 'Any' },
                    { value: 1, label: '1+' },
                    { value: 2, label: '2+' },
                    { value: 3, label: '3+' },
                    { value: 4, label: '4+' },
                    { value: 5, label: '5' }
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            </FormControl>

            {/* Sort By */}
            <FormControl sx={{ mb: 3, width: '100%' }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="rating-high">Rating: High to Low</MenuItem>
                <MenuItem value="rating-low">Rating: Low to High</MenuItem>
                <MenuItem value="distance">Distance: Nearest First</MenuItem>
                <MenuItem value="name">Name: A to Z</MenuItem>
                <MenuItem value="price-low">Price: Low to High</MenuItem>
                <MenuItem value="price-high">Price: High to Low</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFilterDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setFilterDialogOpen(false);
            }}
            variant="contained"
            sx={{ backgroundColor: getOfferTypeColor() }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </Box>
  );
};

export default Offers;
